"use client";

import React, { useRef, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { showErrorToast, showSuccessToast } from "@/components/Toast/Toast";
import { ToastContainer } from "react-toastify";
import {
	GetCountries,
	GetState,
} from "react-country-state-city";

type FormData = {
	email: string;
	name: string;
	country: string;
	state: string;
	address1: string;
	address2: string;
	pincode: string;
	organization: string;
	serialNumber: string;
	file: File;
};

type Country = {
	id: number;
	name: string;
	iso3: string;
	iso2: string;
	numeric_code: string;
	phone_code: string;
	capital: string;
	currency: string;
	currency_name: string;
	currency_symbol: string;
	tld: string;
	native: string;
	region: string;
	subregion: string;
	latitude: string;
	longitude: string;
	emoji: string;
}

type State = {
	id: number;
	name: string;
	state_code: string;
}

const Register: React.FC = () => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	let token = useRef("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { register, handleSubmit, setValue, reset } = useForm<FormData>({
		defaultValues: {
			country: "India",
			state: "Andaman and Nicobar Islands"
		}
	});
	const [countryid, setCountryid] = useState(101);
	const [stateid, setStateid] = useState(0);
	const [countriesList, setCountriesList] = useState<Country[]>([]);
	const [stateList, setStateList] = useState<State[]>([]);

	React.useEffect(() => {
		GetCountries().then((result) => {
			if (Array.isArray(result)) {
				setCountriesList(result);
			} else {
				console.error("Expected array from GetCountries, got:", result);
				setCountriesList([]);
			}
		}).catch((err) => {
			console.error("Error fetching countries:", err);
			setCountriesList([]);
		});
		GetState(countryid).then((result) => {
			if (Array.isArray(result)) {
				setStateList(result);
			} else {
				console.error("Expected array from GetState, got:", result);
				setStateList([]);
			}
		}).catch((err) => {
			console.error("Error fetching states:", err);
			setStateList([]);
		});
	}, [countryid]);

	const handleFileChange = (event) => {
		setSelectedFile(event.target.files[0]);
	};

	const onSubmit = async (data: FormData) => {
		try {
			setIsModalOpen(true)
			const serialNumberRegex = /^[EP][CVP][A-Z][0-9]{12,13}$/;
			let pincode: string;
			let state: string;
			const serialNumber = String(data.serialNumber).trim();
			if (!serialNumberRegex.test(serialNumber)) {
				console.log(serialNumber)
				showErrorToast("Invalid serial number format.");
				return;
			}

			if (data.pincode === "") {
				data.pincode = "NA"
			}
			else {
				pincode = data.pincode
			}

			if (stateList.length === 0) {
				data.state = "NA"
			}
			else {
				state = data.state
			}
			data.file = selectedFile!;
			data.address1.concat(data.address2)
			try {

				const response = await axios.post("https://saintiant-precision-5860-tower.tail6bd07e.ts.net/middleware/registerUser", data, {
					headers: { "Content-Type": "multipart/form-data" }
				});

				showSuccessToast(response.data.message);
				reset();
				setUploadProgress(0);
				setIsModalOpen(false);

			} catch (error: any) {
				console.error("API Error:", error);
				setIsModalOpen(false);

				if (error.response) {
					showErrorToast(error.response.data.error || "Request failed");
				} else {
					showErrorToast("Network error");
				}
			}
		} catch (error) {
			console.error("Error during upload:", error);
			showErrorToast("Error during upload");
			setIsModalOpen(false)
		}

	}

	return (
		< div className="flex min-h-screen items-center justify-center bg-gray-100" >
			<div className="w-full max-w-7xl rounded-lg bg-white p-4 shadow-lg">
				<h1 className="mb-6 text-center text-2xl font-bold text-gray-800">
					Registration Form
				</h1>
				<form className="mx-auto w-full max-w-6xl" onSubmit={handleSubmit(onSubmit)}>

					<div className="flex flex-wrap gap-4">
						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="email" className="block text-base font-medium text-black mb-2">
								Email <span className="text-red-500">*</span>
							</label>
							<input
								type="email"
								placeholder="Email"
								className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("email", { required: true })}
							/>
						</div>

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="name" className="block text-base font-medium text-black mb-2">
								Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								placeholder="Name"
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("name", { required: true })}
							/>
						</div>

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="country" className="block text-base font-medium text-black mb-2">
								Country <span className="text-red-500">*</span>
							</label>
							<select
								onChange={(e) => {
									try {
										const country = Array.isArray(countriesList) ? countriesList.find(item => item.id.toString() === e.target.value) : undefined;
										if (country) {
											setCountryid(country.id);
											setStateid(0);
											setValue("country", country.name);
											GetState(country.id).then((result) => {
												if (Array.isArray(result)) {
													setStateList(result);
												} else {
													console.error("Expected array from GetState, got:", result);
													setStateList([]);
												}
											}).catch((err) => {
												console.error("Error fetching states:", err);
												setStateList([]);
											});
										} else {
											setCountryid(101);
										}
									} catch (err) {
										console.error("Error in country onChange:", err);
									}
								}}
								value={countryid}
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
							>
								{Array.isArray(countriesList) && countriesList.length > 0 ? countriesList.map((item) => (
									<option key={item.id} value={item.id}>
										{item.name}
									</option>
								)) : <option disabled>Loading...</option>}
							</select>
						</div>

						{stateList.length > 0 && (
							<div className="flex-1 min-w-[calc(50%-1rem)]">
								<label htmlFor="state" className="block text-base font-medium text-black mb-2">
									State <span className="text-red-500">*</span>
								</label>
								<select
									onChange={(e) => {
										try {
											if (!Array.isArray(stateList) || stateList.length === 0) return;
											const state = stateList[Number(e.target.value)];
											if (!state) return;
											const idx = stateList.findIndex(s => s.id === state.id)
											setValue("state", state.name);
											setStateid(idx >= 0 ? idx : 0);
										} catch (err) {
											console.error("Error in state onChange:", err);
										}
									}}
									value={stateid}
									className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								>
									{Array.isArray(stateList) && stateList.length > 0 ? stateList.map((item, index) => (
										<option key={index} value={index}>
											{item.name}
										</option>
									)) : null}
								</select>
							</div>
						)}

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="address" className="block text-base font-medium text-black mb-2">
								Address <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								placeholder="Flat No. / Building"
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("address1", { required: true })}
							/>
						</div>

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<input
								type="text"
								placeholder="Street Name / City"
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("address2", { required: true })}
							/>
						</div>

						{countryid === 101 && (
							<div className="flex-1 min-w-[calc(50%-1rem)]">
								<label htmlFor="pincode" className="block text-base font-medium text-black mb-2">
									Pincode <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									placeholder="Pincode"
									className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
									{...register("pincode", { required: true })}
								/>
							</div>
						)}

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="organization" className="block text-base font-medium text-black mb-2">
								Organization/Clinic Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								placeholder="Organization/Clinic Name"
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("organization", { required: true })}
							/>
						</div>

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="serialNumber" className="block text-base font-medium text-black mb-2">
								Serial Number <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								placeholder="Serial Number"
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
								{...register("serialNumber", { required: true })}
							/>
						</div>

						<div className="flex-1 min-w-[calc(50%-1rem)]">
							<label htmlFor="file" className="block text-base font-medium text-black mb-2">
								Upload Invoice (Optional)
							</label>
							<input
								type="file"
								onChange={handleFileChange}
								className="w-full rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 outline-none focus:border-blue-500 focus:shadow-md"
							/>
						</div>
					</div>
					<button
						className="hover:shadow-form rounded-md bg-saintiantblue px-8 py-3 text-base font-semibold text-white outline-none mt-10"
					>
						Submit
					</button>
					{isModalOpen && (
						<div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
							<div className="w-80 bg-white p-6 rounded-lg shadow-lg">
								<h2 className="text-center text-xl font-semibold mb-4">Uploading...</h2>
								<progress
									className="w-full h-4 rounded"
									value={uploadProgress}
									max="100"
								>
									{uploadProgress}%
								</progress>
								<p className="text-center mt-2">{uploadProgress}%</p>
							</div>
						</div>
					)}
				</form>
				<ToastContainer style={{ marginTop: "80px" }} />
			</div>
		</div>
	);
};

export default Register;
